import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Sparkles, GraduationCap, ShieldCheck, Gift, Loader2,
  Shield, FileText, ChevronLeft, ChevronRight, FileSignature, Receipt,
  Gauge, Brain, LifeBuoy
} from "lucide-react";

const GRADES = ["4", "5", "6", "7", "8", "9", "10", "11"];
const RELATIONS = [
  { id: "madre", label: "Madre" },
  { id: "padre", label: "Padre" },
  { id: "responsable_legal", label: "Responsable legal" },
  { id: "tutor_autorizado", label: "Tutor autorizado" },
];

const initialResponsible = { name: "", dui: "", phone: "", email: "", relation: "" };
const initialConsent = {
  authorize_registration: false,
  authorize_ai: false,
  authorize_data_treatment: false,
  confirm_truthful_info: false,
  understand_modification_rights: false,
  signature: "",
};
const initialInvoice = {
  type: "consumidor_final",
  nit: "", nrc: "", business_name: "", economic_activity: "",
};

// ─── Step indicator ────────────────────────────────────────────────────────
const StepDots = ({ step, total }) => (
  <div className="flex items-center gap-2" data-testid="step-dots">
    {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
      <span
        key={n}
        className={`h-1.5 rounded-full transition-all ${
          n === step ? "w-7 bg-indigo-600" : n < step ? "w-3 bg-indigo-300" : "w-3 bg-slate-200"
        }`}
      />
    ))}
  </div>
);

// ─── Wizard page ────────────────────────────────────────────────────────────
const PurchaseLanding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ schools: [], products: [], mock_mode: false });
  const [step, setStep] = useState(1);

  const [responsible, setResponsible] = useState(initialResponsible);
  const [consent, setConsent] = useState(initialConsent);
  const [purchase, setPurchase] = useState({ product_code: "tplus_2026", school_id: "", student_name: "", grade: "" });
  const [invoice, setInvoice] = useState(initialInvoice);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/payments/schools-with-b2c`);
        if (!cancelled) {
          setData(r.data);
          if ((r.data.schools || []).length === 1) {
            setPurchase((p) => ({ ...p, school_id: r.data.schools[0].id }));
          }
        }
      } catch (e) {
        toast.error("No se pudo cargar la información de compras");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedProduct = useMemo(
    () => (data.products || []).find((p) => p.code === purchase.product_code) || (data.products || [])[0],
    [data.products, purchase.product_code]
  );

  const allConsents = useMemo(
    () => Object.entries(consent).filter(([k]) => k.startsWith("authorize_") || k.startsWith("confirm_") || k.startsWith("understand_"))
                                  .every(([, v]) => v === true),
    [consent]
  );

  const canProceed = () => {
    if (step === 1) {
      const okBasic = purchase.product_code && purchase.school_id && purchase.student_name.trim().length >= 2 && purchase.grade;
      const okInvoice = invoice.type === "consumidor_final" ||
        (invoice.nit.trim() && invoice.nrc.trim() && invoice.business_name.trim() && invoice.economic_activity.trim());
      return okBasic && okInvoice;
    }
    if (step === 2) {
      const r = responsible;
      return r.name.trim().length >= 3 && r.dui.trim().length >= 8 &&
             r.phone.trim().length >= 6 && /.+@.+\..+/.test(r.email) && r.relation;
    }
    if (step === 3) {
      return allConsents && consent.signature.trim().length >= 3;
    }
    return false;
  };

  const next = () => {
    if (!canProceed()) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!canProceed()) return;
    setSubmitting(true);
    try {
      const payload = {
        product_code: purchase.product_code,
        school_id: purchase.school_id,
        student_name: purchase.student_name,
        grade: purchase.grade,
        invoice,
        responsible,
        consent,
      };
      const r = await axios.post(`${API}/payments/checkout`, payload);
      toast.success("Redirigiendo al pago…");
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar el pago");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!selectedProduct || (data.schools || []).length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-cyan-50 px-4 text-center">
        <Sparkles className="w-12 h-12 text-indigo-400 mb-4" />
        <h1 className="text-2xl font-bold text-slate-900">Compras aún no disponibles</h1>
        <p className="text-slate-600 max-w-md mt-2">
          El módulo de compras está deshabilitado. Si eres parte de una institución que ofrecerá QHUB.BUDDY, contáctanos.
        </p>
        <Button className="mt-6" onClick={() => navigate("/contacto")} data-testid="goto-contact">
          Contáctanos
        </Button>
      </div>
    );
  }

  const p = selectedProduct;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Top promo banner */}
      <div className="w-full bg-gradient-to-r from-rose-500 via-amber-500 to-rose-500 text-white text-center py-2.5 px-4 shadow-md"
           data-testid="promo-banner-2026">
        <p className="text-sm sm:text-base font-bold uppercase tracking-[0.2em]">
          🎉 Oferta 2026
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-10 lg:py-14">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <GraduationCap className="w-7 h-7 text-indigo-600" />
            <div>
              <p className="text-xs font-semibold tracking-wider text-indigo-700 uppercase">QHUB.BUDDY</p>
              <h1 className="text-lg font-bold text-slate-900 leading-tight">Compra T+ Trimestre Plus</h1>
            </div>
          </div>
          <StepDots step={step} total={3} />
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Sidebar — plan selector + summary */}
          <Card className="md:col-span-1 border-indigo-100 shadow-lg shadow-indigo-100/40 h-fit md:sticky md:top-6">
            <CardContent className="p-6">
              <p className="text-xs font-semibold tracking-wider text-slate-500 uppercase mb-3">
                Elige tu plan
              </p>

              <div className="space-y-2.5">
                {(data.products || []).map((prod) => {
                  const active = purchase.product_code === prod.code;
                  return (
                    <button
                      key={prod.code}
                      type="button"
                      onClick={() => setPurchase((s) => ({ ...s, product_code: prod.code }))}
                      className={`relative w-full text-left p-3.5 rounded-xl border-2 transition ${
                        active
                          ? "border-indigo-500 bg-indigo-50/60 shadow-sm"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                      data-testid={`plan-card-${prod.code}`}
                    >
                      {prod.offer_label && (
                        <span className="absolute -top-2 -right-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-md"
                              data-testid={`plan-offer-${prod.code}`}>
                          {prod.offer_label}
                        </span>
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-bold text-sm text-slate-900">{prod.name}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">{prod.subtitle}</p>
                        </div>
                        <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 ${
                          active ? "border-indigo-600 bg-indigo-600" : "border-slate-300"
                        }`} />
                      </div>
                      <div className="mt-3">
                        <p className={`text-lg font-extrabold leading-tight ${active ? "text-indigo-700" : "text-slate-700"}`}>
                          {prod.daily_label}
                        </p>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        ${prod.price.toFixed(2)} USD · {prod.duration_days === 30 ? "1 mes" : `${Math.round(prod.duration_days / 30)} meses`}
                      </p>
                    </button>
                  );
                })}
              </div>

              <ul className="text-xs text-slate-700 mt-5 space-y-3 pt-4 border-t border-slate-100">
                <li className="flex items-start gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Gauge className="w-3.5 h-3.5 text-indigo-600" />
                  </span>
                  <span className="leading-tight pt-1">Aprendizaje a su propio ritmo</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-3.5 h-3.5 text-purple-600" />
                  </span>
                  <span className="leading-tight pt-1">Pensamiento propio</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
                    <LifeBuoy className="w-3.5 h-3.5 text-cyan-600" />
                  </span>
                  <span className="leading-tight pt-1">Portal de acompañamiento</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  </span>
                  <span className="leading-tight pt-1">Entorno seguro y protegido</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                  </span>
                  <span className="leading-tight pt-1"><b>+</b> Acceso del padre/madre al dashboard</span>
                </li>
              </ul>

              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                Pago seguro · n1co
              </div>
              {data.mock_mode && (
                <div className="mt-3 text-[10px] bg-amber-50 text-amber-700 px-2.5 py-2 rounded-lg border border-amber-200">
                  Modo demo activo
                </div>
              )}
            </CardContent>
          </Card>

          {/* Wizard content */}
          <Card className="md:col-span-2 shadow-lg">
            <CardContent className="p-7">
              {step === 1 && (
                <Step3Purchase
                  schools={data.schools}
                  purchase={purchase}
                  setPurchase={setPurchase}
                  invoice={invoice}
                  setInvoice={setInvoice}
                  product={p}
                />
              )}
              {step === 2 && (
                <Step1Responsible
                  responsible={responsible}
                  setResponsible={setResponsible}
                />
              )}
              {step === 3 && (
                <Step2Consent
                  responsible={responsible}
                  consent={consent}
                  setConsent={setConsent}
                />
              )}

              {/* Nav buttons */}
              <div className="flex items-center justify-between gap-2 mt-7 pt-5 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={back}
                  disabled={step === 1 || submitting}
                  className="gap-1"
                  data-testid="wizard-back"
                >
                  <ChevronLeft className="w-4 h-4" /> Atrás
                </Button>

                {step < 3 ? (
                  <Button
                    onClick={next}
                    disabled={!canProceed()}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
                    data-testid="wizard-next"
                  >
                    Continuar <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={submit}
                    disabled={!canProceed() || submitting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                    data-testid="wizard-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pagar $${p.price.toFixed(2)} con n1co`}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ─── Step 1 — Responsable Legal ────────────────────────────────────────────
const Step1Responsible = ({ responsible, setResponsible }) => {
  const set = (k, v) => setResponsible((r) => ({ ...r, [k]: v }));
  return (
    <div data-testid="step-1">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
          <Shield className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Datos del Responsable Legal</h2>
          <p className="text-sm text-slate-500">Información del padre, madre o tutor (obligatorio)</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-sm font-medium">Nombre completo *</Label>
          <Input
            value={responsible.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Nombre completo del responsable"
            data-testid="resp-name"
            className="mt-1"
          />
        </div>

        <div>
          <Label className="text-sm font-medium">Número de DUI *</Label>
          <Input
            value={responsible.dui}
            onChange={(e) => set("dui", e.target.value)}
            placeholder="00000000-0"
            data-testid="resp-dui"
            className="mt-1"
            maxLength={14}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-medium">Teléfono *</Label>
            <Input
              value={responsible.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+503 0000-0000"
              data-testid="resp-phone"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Correo electrónico *</Label>
            <Input
              type="email"
              value={responsible.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="correo@ejemplo.com"
              data-testid="resp-email"
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium">Relación con el estudiante *</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {RELATIONS.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => set("relation", r.id)}
                className={`p-3 rounded-lg border-2 text-left text-sm transition ${
                  responsible.relation === r.id
                    ? "border-indigo-500 bg-indigo-50 text-indigo-700 font-medium"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`resp-relation-${r.id}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Step 2 — Consentimiento Informado ─────────────────────────────────────
const Step2Consent = ({ responsible, consent, setConsent }) => {
  const toggle = (k) => setConsent((c) => ({ ...c, [k]: !c[k] }));
  const items = [
    { k: "authorize_registration", label: "Autorizo el registro del estudiante en el ecosistema educativo QHUB.BUDDY." },
    { k: "authorize_ai", label: <span>Autorizo el uso de herramientas de <b>inteligencia artificial</b> con fines exclusivamente educativos.</span> },
    { k: "authorize_data_treatment", label: <span>Autorizo el tratamiento de los datos personales del menor conforme a la legislación aplicable en El Salvador, incluyendo la <b>LEPINA</b> y la <b>Ley Crecer Juntos</b>.</span> },
    { k: "confirm_truthful_info", label: <span>Confirmo que la información proporcionada es <b>verídica</b>.</span> },
    { k: "understand_modification_rights", label: <span>Comprendo que puedo solicitar la <b>modificación o eliminación</b> de los datos en cualquier momento.</span> },
  ];

  return (
    <div data-testid="step-2">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
          <FileText className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Consentimiento Informado</h2>
          <p className="text-sm text-slate-500">Autorización legal para el registro</p>
        </div>
      </div>

      <div className="bg-slate-50 rounded-xl p-4 mb-4 text-sm">
        <p>Yo, <b className="text-indigo-700">{responsible.name || "—"}</b>,</p>
        <p className="text-slate-600 mt-1">
          en calidad de <b>{(RELATIONS.find(r => r.id === responsible.relation) || {}).label || "—"}</b> del menor antes identificado, declaro que:
        </p>
      </div>

      <div className="space-y-3">
        {items.map(({ k, label }) => (
          <label
            key={k}
            className="flex gap-3 items-start cursor-pointer p-3 rounded-lg hover:bg-slate-50 transition"
            data-testid={`consent-${k}`}
          >
            <Checkbox checked={consent[k]} onCheckedChange={() => toggle(k)} className="mt-0.5" />
            <span className="text-sm text-slate-700">{label}</span>
          </label>
        ))}
      </div>

      <div className="mt-5 bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-sm">
        <p className="font-semibold text-indigo-900 flex items-center gap-2">
          <Shield className="w-4 h-4" /> Protección de Datos
        </p>
        <ul className="mt-2 text-xs text-indigo-800 space-y-1 list-disc list-inside">
          <li>Los datos serán utilizados exclusivamente con fines educativos.</li>
          <li>No serán vendidos ni compartidos sin autorización.</li>
          <li>Se implementan medidas de seguridad para proteger la información.</li>
          <li>El acceso está restringido a personal autorizado.</li>
        </ul>
      </div>

      <div className="mt-5">
        <Label className="text-sm font-medium flex items-center gap-2">
          <FileSignature className="w-4 h-4" /> Firma del Responsable Legal *
        </Label>
        <Input
          value={consent.signature}
          onChange={(e) => setConsent({ ...consent, signature: e.target.value })}
          placeholder="Escribe tu nombre completo como firma electrónica"
          className="mt-1 font-serif italic text-lg"
          data-testid="consent-signature"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Al escribir tu nombre confirmas la aceptación legal del consentimiento.
        </p>
      </div>
    </div>
  );
};

// ─── Step 3 — Compra + Facturación ─────────────────────────────────────────
const Step3Purchase = ({ schools, purchase, setPurchase, invoice, setInvoice, product }) => {
  const set = (k, v) => setPurchase((p) => ({ ...p, [k]: v }));
  const setI = (k, v) => setInvoice((i) => ({ ...i, [k]: v }));
  const isCF = invoice.type === "credito_fiscal";

  return (
    <div data-testid="step-3">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
          <Receipt className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Datos de compra y facturación</h2>
          <p className="text-sm text-slate-500">Confirma los datos del estudiante y selecciona el tipo de factura</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-sm font-medium">Institución *</Label>
          <Select value={purchase.school_id} onValueChange={(v) => set("school_id", v)}>
            <SelectTrigger data-testid="select-school" className="mt-1">
              <SelectValue placeholder="Selecciona institución" />
            </SelectTrigger>
            <SelectContent>
              {schools.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`school-option-${s.id}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-medium">Nombre del estudiante *</Label>
            <Input
              value={purchase.student_name}
              onChange={(e) => set("student_name", e.target.value)}
              placeholder="Nombre completo del estudiante"
              data-testid="input-student-name"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Grado *</Label>
            <Select value={purchase.grade} onValueChange={(v) => set("grade", v)}>
              <SelectTrigger data-testid="select-grade" className="mt-1">
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent>
                {GRADES.map((g) => (
                  <SelectItem key={g} value={g} data-testid={`grade-option-${g}`}>{g}°</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="pt-3 border-t border-slate-100">
          <Label className="text-sm font-medium">Tipo de factura *</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => setInvoice({ ...initialInvoice, type: "consumidor_final" })}
              className={`p-4 rounded-lg border-2 text-left transition ${
                invoice.type === "consumidor_final"
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="invoice-type-cf"
            >
              <p className="font-medium text-sm">Consumidor Final</p>
              <p className="text-xs text-slate-500 mt-0.5">Comprobante personal</p>
            </button>
            <button
              type="button"
              onClick={() => setI("type", "credito_fiscal")}
              className={`p-4 rounded-lg border-2 text-left transition ${
                invoice.type === "credito_fiscal"
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="invoice-type-cf-fiscal"
            >
              <p className="font-medium text-sm">Crédito Fiscal</p>
              <p className="text-xs text-slate-500 mt-0.5">Para empresas (con NIT/NRC)</p>
            </button>
          </div>
        </div>

        {isCF && (
          <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid="cf-fields">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">NIT *</Label>
                <Input value={invoice.nit} onChange={(e) => setI("nit", e.target.value)}
                       placeholder="0000-000000-000-0" data-testid="cf-nit" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium">NRC *</Label>
                <Input value={invoice.nrc} onChange={(e) => setI("nrc", e.target.value)}
                       placeholder="000000-0" data-testid="cf-nrc" className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Razón social *</Label>
              <Input value={invoice.business_name} onChange={(e) => setI("business_name", e.target.value)}
                     placeholder="Nombre de la empresa" data-testid="cf-business" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-medium">Giro económico *</Label>
              <Input value={invoice.economic_activity} onChange={(e) => setI("economic_activity", e.target.value)}
                     placeholder="Ej: Educación, Comercio, Servicios" data-testid="cf-activity" className="mt-1" />
            </div>
          </div>
        )}

        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-emerald-700 uppercase tracking-wider">Total a pagar</p>
            <p className="text-2xl font-extrabold text-emerald-900">${product.price.toFixed(2)} <span className="text-sm font-normal text-emerald-700">USD</span></p>
            <p className="text-[11px] text-emerald-700 mt-0.5">{product.name} · {product.duration_days === 30 ? "1 mes" : `${Math.round(product.duration_days / 30)} meses`} de acceso</p>
          </div>
          <Badge className="bg-emerald-200 text-emerald-800">Pago único</Badge>
        </div>
      </div>
    </div>
  );
};

// ---- Mock checkout page ----
export const PurchaseMockCheckout = () => {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const amount = params.get("amount");
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  const simulate = async (success) => {
    if (!success) { navigate(`/comprar/error?ref=${ref}`); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/payments/_mock-emit-success/${ref}`);
      navigate(`/comprar/exito?ref=${ref}`);
    } catch (e) {
      toast.error("Mock webhook falló");
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center px-4">
      <Card className="max-w-md w-full bg-slate-800 border-slate-700 text-white">
        <CardContent className="p-7">
          <Badge className="bg-amber-500 text-slate-900 mb-3">MOCK · simulación local</Badge>
          <h2 className="text-xl font-bold">Pasarela n1co (demo)</h2>
          <p className="text-sm text-slate-400 mt-1">Orden #{ref?.slice(0, 8)}</p>
          <div className="text-3xl font-extrabold mt-5">${amount} <span className="text-base font-normal text-slate-400">USD</span></div>
          <p className="text-xs text-slate-500 mt-2">Este pago es ficticio. En producción aquí aparecería el formulario de tarjeta de n1co.</p>
          <div className="flex gap-2 mt-6">
            <Button onClick={() => simulate(true)} disabled={processing}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="mock-pay-success">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simular pago exitoso"}
            </Button>
            <Button variant="outline" onClick={() => simulate(false)} disabled={processing}
                    className="flex-1 bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700"
                    data-testid="mock-pay-cancel">
              Cancelar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ---- Success page ----
export const PurchaseSuccess = () => {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!ref) return;
    let stopped = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/order/${ref}`);
        if (stopped) return;
        setOrder(r.data);
        if (r.data.status !== "paid" && attempts < 20) {
          setTimeout(() => setAttempts(a => a + 1), 1500);
        }
      } catch (e) {
        if (attempts < 20) setTimeout(() => setAttempts(a => a + 1), 2000);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [attempts, ref]);

  const paid = order?.status === "paid";

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-indigo-50 flex items-center justify-center px-4">
      <Card className="max-w-lg w-full shadow-xl">
        <CardContent className="p-8 text-center">
          {paid ? (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <ShieldCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">¡Pago confirmado!</h1>
              <p className="text-slate-600 mt-2">
                Te enviamos un correo con el usuario y contraseña del estudiante.
              </p>
              {order?.created_student_username && (
                <div className="mt-5 bg-slate-50 border border-slate-200 rounded-xl p-4 text-left text-sm">
                  <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Usuario creado</p>
                  <p className="font-mono font-bold text-slate-900">{order.created_student_username}</p>
                  <p className="text-xs text-slate-500 mt-2">La contraseña fue enviada por correo a tu dirección.</p>
                </div>
              )}
              <Button className="mt-6 bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => window.location.href = "/login"}
                      data-testid="goto-login">
                Iniciar sesión
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-indigo-500 mb-4" />
              <h1 className="text-xl font-bold text-slate-900">Confirmando tu pago…</h1>
              <p className="text-sm text-slate-500 mt-2">Esto puede tomar unos segundos. No cierres la página.</p>
              <p className="text-xs text-slate-400 mt-4">Orden: <span className="font-mono">{ref}</span></p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ---- Cancel/error page ----
export const PurchaseError = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-rose-50 via-white to-slate-50">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <h1 className="text-2xl font-bold text-slate-900">Pago no completado</h1>
          <p className="text-slate-600 mt-2">No se procesó ningún cargo. Puedes intentarlo nuevamente.</p>
          <Button className="mt-6" onClick={() => navigate("/comprar")} data-testid="retry-purchase">
            Volver a intentar
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseLanding;
